import type {
  BlockStateDefinition,
  ImplicitDependencyGroup,
} from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import { lazy } from 'react';
import type {
  CalendarBlockRef,
  CalendarComponentType,
} from '@unifyapps/defs/blocks/Calendar/types';
import type { Action } from '@unifyapps/defs/types/action';

const importCalendarComponent = () =>
  import(
    /* webpackChunkName: "calendar-block" */
    './Calendar'
  );

const Calendar = lazy(importCalendarComponent);

class CalenderBlockStateDefinition implements BlockStateDefinition {
  getComponent() {
    return Calendar;
  }

  useBlockRef?: boolean | undefined = true;

  get type(): ComponentTypeUnionType {
    return 'EventsCalendar';
  }

  get initialStateGetter() {
    return (block: BlockType) =>
      ({
        id: block.id,
        ..._pick(block.component as CalendarComponentType, [
          'content',
          'appearance',
          'componentType',
        ]),
      }) as BlockStateUnionType;
  }

  getSlotOrder(block: BlockType) {
    return {
      order: [],
    };
  }

  preload() {
    return {
      blockPreload: importCalendarComponent,
    };
  }

  getBlockControlMethods() {
    return [
      {
        id: 'refetchData' as const,
        label: 'Refetch Data',
        method: async (_: BlockStateUnionType, __: Action, blockRef?: CalendarBlockRef) => {
          await blockRef?.refetch?.();
          return Promise.resolve();
        },
      },
      {
        id: 'unselectItem' as const,
        label: 'Unselect Item',
        method: (blockState: BlockStateUnionType) => {
          return {
            ...blockState,
            selectedItem: undefined,
          };
        },
      },
      {
        id: 'unselectInterval' as const,
        label: 'Unselect Interval',
        method: (blockState: BlockStateUnionType, __: Action, blockRef?: CalendarBlockRef) => {
          blockRef?.unselectInterval?.();
          return {
            ...blockState,
            selectedInterval: undefined,
          };
        },
      },
    ];
  }

  getImplicitDependencyGroups(): ImplicitDependencyGroup {
    return { usingDataSourcesResponse: true, isEntitySuspended: true };
  }
}

export default CalenderBlockStateDefinition;
